class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.mainGallery = null;
    this.thumbsGallery = null;
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.swiperInitialized = false;
  }

  connectedCallback() {
    this.mainGallery = this.querySelector('.product-atf__gallery-main');
    this.thumbsGallery = this.querySelector('.product-atf__gallery-thumbs');

    if (!this.mainGallery || !this.thumbsGallery) return;

    this.onResize = this.debounce(this.onResize.bind(this), 250);
    this.checkViewportAndInitSlider();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.destroy();
  }

  /**
   * Checks current viewport width and initializes or destroys Swiper based on viewport
   */
  checkViewportAndInitSlider() {
    this.isMobile = window.innerWidth < 990;

    if (this.isMobile && !this.swiperInitialized) {
      this.initSwiper();
    } else if (!this.isMobile && this.swiperInitialized) {
      this.destroySwiper();
    }
  }

  /**
   * Initialize Swiper instances for main gallery and thumbnails
   */
  initSwiper() {
    if (this.swiperInitialized) return;

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper not loaded');
      return;
    }

    // Create thumbs swiper first
    this.thumbsSwiper = new Swiper(this.thumbsGallery, {
      slidesPerView: 5,
      spaceBetween: 8,
      watchSlidesProgress: true,
      slideToClickedSlide: false, // Disable default behavior to prevent conflicts
      // threshold: 10,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous thumbnail',
        nextSlideMessage: 'Next thumbnail'
      },
      on: {
        click: (swiper, event) => {
          this.handleThumbnailClick(swiper, event);
        }
      }
    });

    // Initialize main gallery with thumbs as navigation
    this.mainSwiper = new Swiper(this.mainGallery, {
      slidesPerView: 1,
      spaceBetween: 10,
      autoHeight: true,
      thumbs: {
        swiper: this.thumbsSwiper
      },
      grabCursor: true,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous product image',
        nextSlideMessage: 'Next product image'
      }
    });

    this.swiperInitialized = true;
  }

  /**
   * Handle thumbnail click events for auto-scrolling
   * @param {Swiper} swiper - The thumbnail swiper instance
   * @param {Event} event - The click event
   */
  handleThumbnailClick(swiper, event) {
    const clickedSlide = event.target.closest('.swiper-slide');
    if (!clickedSlide) return;

    const clickedIndex = Array.from(swiper.slides).indexOf(clickedSlide);
    const totalSlides = swiper.slides.length;
    const slidesPerView = swiper.params.slidesPerView;

    // Get the current active slide index range that's visible
    const activeIndex = swiper.activeIndex;
    const lastVisibleIndex = Math.min(activeIndex + slidesPerView - 1, totalSlides - 1);
    const firstVisibleIndex = activeIndex;

    // Check if the clicked slide is the last visible one and there are more slides
    if (clickedIndex === lastVisibleIndex && clickedIndex < totalSlides - 1) {
      // Prevent default Swiper behavior
      event.preventDefault();
      event.stopPropagation();

      // Calculate the next slide to show
      const nextSlideIndex = Math.min(clickedIndex + 1, totalSlides - slidesPerView);

      // First change the main gallery to the clicked thumbnail
      this.mainSwiper.slideTo(clickedIndex, 300);

      // Then smoothly slide the thumbnail strip to show next thumbnail
      setTimeout(() => {
        swiper.slideTo(nextSlideIndex, 300);
      }, 50); // Small delay to ensure main gallery slides first
    }
    // Check if the clicked slide is the first visible one and we can scroll backwards
    else if (clickedIndex === firstVisibleIndex && clickedIndex > 0) {
      // Prevent default Swiper behavior
      event.preventDefault();
      event.stopPropagation();

      // Calculate the previous slide to show
      const prevSlideIndex = Math.max(clickedIndex - 1, 0);

      // First change the main gallery to the clicked thumbnail
      this.mainSwiper.slideTo(clickedIndex, 300);

      // Then smoothly slide the thumbnail strip to show previous thumbnail
      setTimeout(() => {
        swiper.slideTo(prevSlideIndex, 300);
      }, 50); // Small delay to ensure main gallery slides first
    } else {
      // For normal clicks (not on edge thumbnails), just change the main gallery
      this.mainSwiper.slideTo(clickedIndex, 300);
    }
  }

  /**
   * Destroy Swiper instances when not needed
   */
  destroySwiper() {
    if (!this.swiperInitialized) return;

    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }

    this.swiperInitialized = false;
  }

  /**
   * Resize event handler
   */
  onResize() {
    this.checkViewportAndInitSlider();
  }

  /**
   * Create a debounce function for better performance
   * @param {Function} func - The function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @return {Function} - Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    window.addEventListener('resize', this.onResize);
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    window.removeEventListener('resize', this.onResize);
    this.destroySwiper();
  }

  /**
   * Method to be called when the section is updated in the customizer
   */
  onSectionUpdate() {
    this.destroySwiper();
    this.checkViewportAndInitSlider();
  }
}


class ProductAccordions extends HTMLElement {
  constructor() {
    super();
    this.accordionTriggers = this.querySelectorAll('.accordion-trigger');
    this.init();
  }

  init() {
    // Initialize event listeners for each accordion trigger
    this.accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.toggleAccordion.bind(this, trigger));
    });

    // Initialize accessible keyboard navigation
    this.initAccessibility();
  }

  toggleAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const controlId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(controlId);

    // Set aria and class attributes
    trigger.setAttribute('aria-expanded', !isExpanded);
    trigger.classList.toggle('active', !isExpanded);

    if (content) {
      if (!isExpanded) {
        // Opening animation
        content.hidden = false;
        content.classList.remove('hidden');
        content.classList.add('active');

        // Calculate the exact height needed
        const scrollHeight = content.scrollHeight;
        content.style.maxHeight = `${scrollHeight}px`;
      } else {
        // Closing animation - first set max-height to current height
        content.style.maxHeight = `${content.scrollHeight}px`;

        // Force a reflow to ensure the browser recognizes the current maxHeight
        void content.offsetWidth;

        // Now set it to 0 for the animation
        content.classList.remove('active');
        content.style.maxHeight = '0';

        // Wait for animation to complete before hiding completely
        setTimeout(() => {
          if (!content.classList.contains('active')) {
            content.classList.add('hidden');
            content.hidden = true;
          }
        }, 300); // Match this to your animation duration in CSS
      }
    }
  }

  closeAllAccordions() {
    this.accordionTriggers.forEach(trigger => {
      const controlId = trigger.getAttribute('aria-controls');
      const content = document.getElementById(controlId);

      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('active');

      if (content) {
        content.classList.remove('active');
        content.classList.add('hidden');
        content.hidden = true;
        content.style.maxHeight = '0';
      }
    });
  }

  initAccessibility() {
    // Add keyboard navigation
    this.accordionTriggers.forEach(trigger => {
      trigger.addEventListener('keydown', (event) => {
        const key = event.key;

        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          this.toggleAccordion(trigger);
        }
      });
    });
  }
}


class ProductPurchaseOptionsHandler extends HTMLElement {
  constructor() {
    super();
    this.shopMoneyFormat = null; // Will be set if Shopify object is available
    this.currentAddonPrice = 0;
    this.mainProductPrice = 0;
    this.mainProductComparePrice = 0;
    this.addonFullPrice = 0; // Store the add-on's full price
    this.atcButtonPriceArea = null;
    this.optionRadios = null;
    this.form = null;
  }

  connectedCallback() {
    if (typeof Shopify !== 'undefined' && Shopify.money_format) {
      this.shopMoneyFormat = Shopify.money_format;
    } else {
      console.error('Shopify money format not available.');
      // Fallback or handle error - for now, operations requiring it will fail gracefully.
    }

    this.atcButtonPriceArea = this.querySelector('[data-js-product-price-extended]');
    this.optionRadios = this.querySelectorAll('.option-radio');
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');

    if (!this.atcButtonPriceArea) {
      console.warn('ProductPurchaseOptionsHandler: ATC button price area not found in container:', this);
    }
    if (this.optionRadios.length === 0) {
      // console.warn('ProductPurchaseOptionsHandler: No option radios found in container:', this);
    }

    this._bindEvents();
    // this._initDefaultPrices();
  }

  disconnectedCallback() {
    this.destroy();
  }

  _refreshAddonPricesFromCheckbox() {
    const addOnCheckbox = this.querySelector('.add-on-capsule-case__checkbox');
    
    // Reset addon prices first
    this.currentAddonPrice = 0;
    this.addonFullPrice = 0;
    
    // Only apply addon prices if checkbox exists, has data, AND is checked
    if (addOnCheckbox && 
        addOnCheckbox.checked && 
        typeof addOnCheckbox.dataset.price !== 'undefined' && 
        typeof addOnCheckbox.dataset.fullPrice !== 'undefined') {
      
      const priceInCents = parseInt(addOnCheckbox.dataset.price, 10);
      const fullPriceInCents = parseInt(addOnCheckbox.dataset.fullPrice, 10);
      
      this.currentAddonPrice = !isNaN(priceInCents) ? priceInCents : 0;
      this.addonFullPrice = !isNaN(fullPriceInCents) ? fullPriceInCents : 0;
      
      console.log('Addon checkbox checked - applying prices:', {
        currentAddonPrice: this.currentAddonPrice,
        addonFullPrice: this.addonFullPrice
      });
    } else {
      console.log('Addon checkbox not checked or not found - no addon prices');
    }
  }

  _updateMainProductPricesFromOption(selectedOptionLabel) {
    const priceStr = selectedOptionLabel.dataset.price;
    const fullPriceStr = selectedOptionLabel.dataset.fullPrice;
    console.log('priceStr', priceStr);
    console.log('fullPriceStr', fullPriceStr);
    
    // Validate price data before proceeding
    if (!priceStr || priceStr === 'undefined' || priceStr === 'null') {
      console.error('Invalid price data:', priceStr);
      return;
    }
    
    // priceStr and fullPriceStr are expected to be in cents as per the data attributes in product-purchase-options.liquid
    this.mainProductPrice = parseInt(priceStr, 10) || 0;
    this.mainProductComparePrice = parseInt(fullPriceStr, 10) || 0;

    if (this.mainProductComparePrice <= this.mainProductPrice) {
      this.mainProductComparePrice = 0;
    }

    // Refresh add-on prices directly from checkbox state before rendering
    this._refreshAddonPricesFromCheckbox();

    // Update hidden form inputs
    if (this.form) {
      const productIdInput = this.form.querySelector('input#product_id');
      if (productIdInput && selectedOptionLabel.dataset.productId) {
        productIdInput.value = selectedOptionLabel.dataset.productId;
      }

      const quantityInput = this.form.querySelector('input#quantity');
      if (quantityInput) { // Quantity might not always be on the label directly
        quantityInput.value = selectedOptionLabel.dataset.quantity || '1'; // Default to 1 if not specified
      }

      const sellingPlanInput = this.form.querySelector('input#selling_plan');
      if (sellingPlanInput && selectedOptionLabel.dataset.sellingPlan) {
        sellingPlanInput.value = selectedOptionLabel.dataset.sellingPlan;
      }
      const option0Input = this.form.querySelector('input#option-0');
      if (option0Input && selectedOptionLabel.dataset.sellingPlanName) {
        option0Input.value = selectedOptionLabel.dataset.sellingPlanName;
      }
    }

    // Actually render the prices after updating them
    this._renderPrices(selectedOptionLabel);
  }

  _renderPrices(selectedOptionLabel) {
    // Ensure DisplayPrice function is available
    if (typeof window.DisplayPrice !== 'function') {
      console.warn('DisplayPrice function not available, skipping price update');
      return;
    }

    // Get quantity and product ID
    const quantity = parseInt(selectedOptionLabel.dataset.quantity || '1', 10);
    const productId = selectedOptionLabel.dataset.productId;
    
    // Convert from cents to dollars
    const unitBasePrice = this.mainProductPrice / 100;
    const unitComparePrice = this.mainProductComparePrice > 0 ? this.mainProductComparePrice / 100 : undefined;
    const unitAddOnPrice = this.currentAddonPrice / 100;

    // Only update if we have valid price data
    if (this.mainProductPrice > 0) {
      try {
        window.DisplayPrice(
          unitBasePrice,
          unitComparePrice,
          quantity,
          productId,
          unitAddOnPrice
        );
      } catch (error) {
        console.error('Error calling DisplayPrice:', error);
      }
    } else {
      console.warn('Main product price is 0, not updating display');
    }
  }

  _handleAddonCheckboxChange(event) {
    console.log('Addon checkbox changed:', event.target.checked);
    // Refresh addon prices and re-render
    this._refreshAddonPricesFromCheckbox();
    this._triggerPriceUpdate();
  }

  _handleAdonToggled(event) {
    const { price, fullPrice, isChecked } = event.detail;
    this.currentAddonPrice = isChecked ? parseInt(price, 10) : 0;
    this.addonFullPrice = isChecked ? parseInt(fullPrice, 10) : 0;
    
    // Re-render prices when addon is toggled
    this._triggerPriceUpdate();
  }

  _triggerPriceUpdate() {
    // Find the currently selected radio button and re-render prices
    const checkedRadio = this.querySelector('.option-radio:checked');
    if (checkedRadio) {
      const label = checkedRadio.closest('product-delivery-option');
      if (label) {
        this._renderPrices(label);
      }
    }
  }

  _handleOptionChange(event) {
    if (event.target.checked) {
      const label = event.target.closest('product-delivery-option');
      if (label) {
        this._updateMainProductPricesFromOption(label);
      }
    }
  }

  _bindEvents() {
    // Bind methods to this instance to ensure correct `this` context
    this.boundHandleAdonToggled = this._handleAdonToggled.bind(this);
    this.boundHandleOptionChange = this._handleOptionChange.bind(this);
    this.boundHandleAddonCheckboxChange = this._handleAddonCheckboxChange.bind(this);

    document.addEventListener('addon:toggled', this.boundHandleAdonToggled);
    this.optionRadios.forEach(radio => {
      radio.addEventListener('change', this.boundHandleOptionChange);
    });

    // Add direct event listener for addon checkbox changes
    const addOnCheckbox = this.querySelector('.add-on-capsule-case__checkbox');
    if (addOnCheckbox) {
      addOnCheckbox.addEventListener('change', this.boundHandleAddonCheckboxChange);
    }
  }

  _initDefaultPrices() {
    // Refresh addon prices from checkbox first. This sets this.currentAddonPrice and this.addonFullPrice.
    this._refreshAddonPricesFromCheckbox();

    const initiallyCheckedRadio = this.querySelector('.option-radio:checked');
    if (initiallyCheckedRadio) {
      const initialLabel = initiallyCheckedRadio.closest('product-delivery-option');
      console.log(initialLabel, 'firstLabel');
      this._updateMainProductPricesFromOption(initialLabel); // This will call _renderPrices
    } else if (this.optionRadios.length > 0) {
      // If no option is checked, select the first one and update form inputs
      this.optionRadios[0].checked = true;
      const firstLabel = this.optionRadios[0].closest('product-delivery-option');
      console.log(firstLabel, 'firstLabel');
      if (firstLabel) {
        this._updateMainProductPricesFromOption(firstLabel); // This will call _renderPrices
        
        // Also update the form inputs directly
        this._updateFormInputs(firstLabel);
      }
    }
    
    // Additional fallback - ensure form inputs are populated even if pricing fails
    setTimeout(() => {
      this._ensureFormInputsPopulated();
    }, 100);
  }
  
  _updateFormInputs(optionElement) {
    if (!this.form) return;
    
    const productId = optionElement.dataset.productId;
    const sellingPlanId = optionElement.dataset.sellingPlan;
    const sellingPlanName = optionElement.dataset.sellingPlanName;
    const quantity = optionElement.dataset.quantity || '1';
    
    const productIdInput = this.form.querySelector('input[name="id"]');
    const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    const optionInput = this.form.querySelector('input[name="option-0"]');
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    
    if (productIdInput && productId) productIdInput.value = productId;
    if (sellingPlanInput) sellingPlanInput.value = sellingPlanId || '';
    if (optionInput) optionInput.value = sellingPlanName || '';
    if (quantityInput) quantityInput.value = quantity;
    
    console.log('Form inputs updated from ProductPurchaseOptionsHandler:', {
      id: productIdInput?.value,
      selling_plan: sellingPlanInput?.value,
      option_0: optionInput?.value,
      quantity: quantityInput?.value
    });
  }
  
  _ensureFormInputsPopulated() {
    // Final check to ensure form inputs are populated
    const productIdInput = this.form?.querySelector('input[name="id"]');
    const sellingPlanInput = this.form?.querySelector('input[name="selling_plan"]');
    const optionInput = this.form?.querySelector('input[name="option-0"]');
    const quantityInput = this.form?.querySelector('input[name="quantity"]');
    
    // If any critical inputs are empty, try to populate them
    if (!productIdInput?.value || !quantityInput?.value) {
      const checkedRadio = this.querySelector('.option-radio:checked');
      if (checkedRadio) {
        const optionElement = checkedRadio.closest('product-delivery-option');
        if (optionElement) {
          this._updateFormInputs(optionElement);
        }
      }
    }
  }

  destroy() {
    document.removeEventListener('addon:toggled', this.boundHandleAdonToggled);
    this.optionRadios.forEach(radio => {
      radio.removeEventListener('change', this.boundHandleOptionChange);
    });

    // Clean up addon checkbox event listener
    const addOnCheckbox = this.querySelector('.add-on-capsule-case__checkbox');
    if (addOnCheckbox && this.boundHandleAddonCheckboxChange) {
      addOnCheckbox.removeEventListener('change', this.boundHandleAddonCheckboxChange);
    }
    
    // console.log('ProductPurchaseOptionsHandler destroyed for container:', this);
  }
}

// Register the custom elements
if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

if (!customElements.get('product-accordions')) {
  customElements.define('product-accordions', ProductAccordions);
}

if (!customElements.get('product-purchase-options')) {
  customElements.define('product-purchase-options', ProductPurchaseOptionsHandler);
}
